/**
 * Тонкий HTTP-слой поверх fetch: JSON in/out, bearer-токен, разбор
 * ошибок сервера. Порт `Services/APIClient.swift` из iOS-клиента —
 * оба клиента обязаны одинаково понимать ответы бэкенда.
 */

const TOKEN_KEY = 'cardbazar.token'

export const API_BASE_URL: string = (
  import.meta.env.VITE_API_BASE_URL || '/api/v1'
).replace(/\/+$/, '')

export type AppErrorKind =
  | 'notAuthenticated'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'network'
  | 'server'

export class AppError extends Error {
  readonly kind: AppErrorKind
  readonly status: number | null

  constructor(
    kind: AppErrorKind,
    message: string,
    status: number | null = null
  ) {
    super(message)
    this.name = 'AppError'
    this.kind = kind
    this.status = status
  }
}

export function readToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    // Приватный режим может запрещать storage — работаем без него.
    return null
  }
}

export function writeToken(token: string | null): void {
  try {
    if (token === null) localStorage.removeItem(TOKEN_KEY)
    else localStorage.setItem(TOKEN_KEY, token)
  } catch {
    /* см. readToken */
  }
}

function kindFor(status: number): AppErrorKind {
  if (status === 401) return 'notAuthenticated'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'notFound'
  if (status === 422) return 'validation'
  return 'server'
}

export async function request<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const token = readToken()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (init.body !== undefined) {
    headers.set('Content-Type', 'application/json')
  }
  if (token !== null) headers.set('Authorization', `Bearer ${token}`)

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, { ...init, headers })
  } catch {
    throw new AppError('network', 'Нет связи с сервером')
  }

  if (response.status === 204) return undefined as T
  const text = await response.text()
  const data = text.length > 0 ? JSON.parse(text) : null

  if (!response.ok) {
    throw new AppError(
      kindFor(response.status),
      data?.message ?? 'Ошибка сервера',
      response.status
    )
  }
  return data as T
}
